import { Button } from "@mui/material";
import { useEffect, useState } from "react";
import { Toast as showToast } from "core/components/global/toast/toast";

// UI
import {
  BootstrapDialog,
  BootstrapDialogInnerContainer,
  DialogActionsButtonContainer,
} from "../../chat.styled";
import CheckboxChecked from "assets/CheckboxChecked";
import CheckboxUnchecked from "assets/CheckboxUnchecked";
import { useTheme } from "styled-components";
import { AnimatePresence, motion } from "framer-motion";
import { Form, FormField } from "../../chat.type";
import { Socket } from "socket.io-client";


interface SuggestedPromptFormProps {
  activeForm: Form;
  setActiveForm: (form: Form | null) => void;
  handleInputSelect: (input: string) => void;
  socket: typeof Socket;
}

export function SuggestedPromptForm({
  activeForm,
  setActiveForm,
  handleInputSelect,
  socket,
}: SuggestedPromptFormProps) {
  const [formState, setFormState] = useState<Record<string, any>>(() => {
    const initialState: Record<string, any> = {};

    if (activeForm?.fields) {
      const initializeFields = (fields: FormField[]) => {
        fields.forEach((field) => {
          if (field.type === "checkbox") {
            initialState[field.name] = !!field.defaultChecked;
            if (field.showFieldsOnCheck) initializeFields(field.showFieldsOnCheck);
            if (field.showFieldsOnUncheck) initializeFields(field.showFieldsOnUncheck);
          } else if (field.type === "text") {
            initialState[`${field.name}_textbox`] = "";
          }
          else if (field.type === "textarea") {
            initialState[`${field.name}_textarea`] = "";
          }
        });
      };
      initializeFields(activeForm.fields as FormField[]);
    }
    return initialState;
  });

  const [expandedFields, setExpandedFields] = useState<Record<string, boolean>>({});
  const theme = useTheme();

  useEffect(() => {
    if (socket) {
      socket.on("prompt_response", (response) => {
        console.log("Received response from prompt:", response);
      });
      return () => {
        socket.off("prompt_response");
      };
    }
  }, [socket]);



  useEffect(() => {
    if (activeForm?.fields) {
      const initialState: Record<string, any> = {};
      const initialExpanded: Record<string, boolean> = {};

      const initializeFields = (fields: FormField[]) => {
        fields.forEach((field) => {
          if (field.type === "checkbox") {
            initialState[field.name] = field.name === "name" ? true : !!field.defaultChecked;
            if (field.showFieldsOnCheck?.length || field.showFieldsOnUncheck?.length) {
              initialExpanded[field.name] = false;
            }
            if (field.showFieldsOnCheck) initializeFields(field.showFieldsOnCheck);
            if (field.showFieldsOnUncheck) initializeFields(field.showFieldsOnUncheck);
          } else if (field.type === "text" || field.type === "textarea") {
            initialState[field.name] = true;
            initialState[`${field.name}_textbox`] = "";
            initialState[`${field.name}_textarea`] = "";
          }
        });
      };

      initializeFields(activeForm.fields as FormField[]);
      setFormState(initialState);
      setExpandedFields(initialExpanded);
    }
  }, [activeForm]);

  const handleChange = (name: string, value: any) => {
    setFormState(prev => {
      const newState = { ...prev, [name]: value };
      let baseKey = "";
      if (name.endsWith("_textbox")) {
        baseKey = name.replace("_textbox", "");
      } else if (name.endsWith("_textarea")) {
        baseKey = name.replace("_textarea", "");
      }

      if (baseKey) newState[baseKey] = value; //Correctly added this line
      return newState;
    });
  };

  const createFlattenedState = () => {
    const flattenedState = { ...formState };
    Object.keys(formState).forEach((key) => {
      if (key.endsWith("_textbox")) {
        const baseKey = key.replace("_textbox", "");
        flattenedState[baseKey] = formState[key];
      }
      else if (key.endsWith("_textarea")) {
        const baseKey = key.replace("_textarea", "");
        flattenedState[baseKey] = formState[key];
      }
    });
    return flattenedState;
  };

  const handleFormSubmit = (e) => {
     const flattenedState = { ...formState };
    e.preventDefault(); 
    const generatedText = activeForm.textOutput(flattenedState);
    console.log(generatedText);
    try {
      if (socket && socket.connected) {
        socket.emit("send_prompt", {
          prompt: generatedText,
          timestamp: new Date().toISOString()
        });
        handleInputSelect(generatedText);
        setActiveForm(null);
      } else {
        showToast("Socket connection not available", "error");
      }
    } catch (error) {
      console.error("Error sending prompt:", error);
      showToast("Error sending prompt to server", "error");
    }
  };
  const toggleExpand = (name: string) => {
    setExpandedFields((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
  };

  const renderField = (field: FormField, isNested: boolean = false): JSX.Element | null => {
    const {
      label,
      name,
      type,
      showFieldsOnCheck = [],
      showFieldsOnUncheck = [],
      showTextFieldsOnCheck = [],
      textboxPlaceholder
    } = field;


    const isChecked = formState[name];
    const hasSubNestedFields = field.showFieldsOnUncheck?.some((child) => child.showFieldsOnCheck);

    const hasNestedFields: boolean = showFieldsOnCheck.length > 0 || showFieldsOnUncheck.length > 0;


    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const checked = e.target.checked;
      handleChange(name, checked);
      const updateChildren = (fields: FormField[]) => {
        fields.forEach((child) => {
          handleChange(child.name, checked);
          if (child.showFieldsOnCheck?.length) updateChildren(child.showFieldsOnCheck);
          if (child.showFieldsOnUncheck?.length) updateChildren(child.showFieldsOnUncheck);
        });
      };
      updateChildren(showFieldsOnCheck);
      updateChildren(showFieldsOnUncheck);
    };

    if (!activeForm || Object.keys(formState).length === 0) {
      return <div>Loading form...</div>; // or null, or spinner etc
    }



    if (type === "checkbox") {
      return (
        <div key={name} style={{ marginLeft: isNested ? "20px" : "0" }}>
          <div className="flex p-[0.5rem]">
            <label
              onClick={() => handleCheckboxChange({ target: { checked: !isChecked } } as any)}
              style={{ display: "inline-flex", alignItems: "center", gap: "8px", cursor: "pointer" }}
            >
              <span style={{ height: "14px", display: "contents" }}>
                <div style={{ transform: "scale(0.75)" }}>
                  {isChecked ? <CheckboxChecked /> : <CheckboxUnchecked theme={theme} />}
                </div>
              </span>
              <span>{label} {label === "L1" ? "*" : ""}</span>
            </label>
            {hasSubNestedFields && isNested && (
              <motion.span
                onClick={() => toggleExpand(name)}
                animate={{ rotate: expandedFields[name] ? 90 : 0 }}
                transition={{ duration: 0.2 }}
                style={{
                  display: "inline-block",
                  cursor: "pointer",
                  marginLeft: "1em",
                  fontSize: "16px",
                  color: "#999",
                }}
              >
                ▶
              </motion.span>

            )}
          </div>


          {showFieldsOnCheck.map((child) => renderField(child, true))}
          <AnimatePresence initial={false}>
            {hasNestedFields && expandedFields[name] && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
              >
                {showFieldsOnUncheck.map((child) => renderField(child, true))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      );
    }

    if (type === "text") {

      return (
        <div key={name} style={{ marginLeft: isNested ? "20px" : "0" }}>
          {textboxPlaceholder ? (
            isChecked && textboxPlaceholder ? (
              <>
                <label style={{ display: "block", marginBottom: "10px" }}>{label}</label>
                <input
                  type="text"
                  value={formState[`${name}_textbox`] || ""}
                  onChange={(e) => handleChange(`${name}_textbox`, e.target.value)}
                  placeholder={textboxPlaceholder}
                  style={{
                    margin: "2px 0 5px 20px", padding: "5px", width: "calc(100% - 40px)",
                  }}
                />
              </>
            ) : null
          ) : (
            <>
              <label style={{ display: "block", marginBottom: "10px" }}>{label}</label>
              <input
                style={{ marginBottom: "10px", width: "calc(100% - 40px)" }}
                type="text"
                value={formState[`${name}_textbox`] || ""}
                onChange={(e) => handleChange(`${name}_textbox`, e.target.value)}
                placeholder={label}
              />

            </>
          )}
        </div>
      );
    }

    if (type === "textarea") {
      return (
        <div key={name} style={{ marginLeft: isNested ? "20px" : "0" }}>
          {textboxPlaceholder ? (
            isChecked && textboxPlaceholder ? (
              <textarea
                value={formState[`${name}_textarea`] || ""}
                onChange={(e) => handleChange(`${name}_textarea`, e.target.value)}
                placeholder={textboxPlaceholder}
                style={{
                  margin: "2px 0 5px 20px", padding: "5px", width: "calc(100% - 40px)",
                }}
                rows={5}
              />
            ) : null
          ) : (
            <>
              <label style={{ display: "block", marginBottom: "10px" }}>{label}</label>
              <textarea
                style={{ marginBottom: "10px", width: "calc(100% - 40px)" }}
                value={formState[`${name}_textarea`] || ""}
                onChange={(e) => handleChange(`${name}_textarea`, e.target.value)}
                placeholder={label}
                rows={5}
              />

            </>
          )}
        </div>
      );
    }



    return null;
  };

  const l1LabelField = activeForm?.fields?.[0]
    ?.showFieldsOnCheck?.[0]
    ?.showFieldsOnUncheck?.find(field => field.label === "L1" && field.type === "checkbox");

  const l1CheckboxName = l1LabelField?.name;
  const l1TextboxName = l1LabelField?.showFieldsOnCheck?.[0]?.name;

  const isL1Checked = !!formState[l1CheckboxName as keyof typeof formState];
  const isL1TextboxFilled = !!formState[`${l1TextboxName}_textbox`] &&
    formState[`${l1TextboxName}_textbox`].trim().length > 0;


  const disableSubmit = false


  return (
    <BootstrapDialog
      open={Boolean(activeForm)}
      onClose={() => setActiveForm(null)}
      sx={{ borderRadius: "1rem" }}
    >
      <BootstrapDialogInnerContainer elevation={2} >
        <div className="p-2">
          <h3 className="ms-3">{activeForm?.title}</h3>
        </div>

        <form onSubmit={handleFormSubmit}>
          <div className="p-4 mt-[-1.5rem] mb-[-1.5rem]"
            style={{
              maxHeight: '55vh',
              overflowY: 'auto',
            }}
          >
            <motion.div
              variants={{
                hidden: { opacity: 0 },
                show: {
                  opacity: 1,
                  transition: {
                    staggerChildren: 0.05,
                  },
                },
              }}
              initial="hidden"
              animate="show"
            >
              {activeForm?.fields.map((child) => (
                <motion.div key={child.name} variants={{ hidden: { opacity: 0, y: 5 }, show: { opacity: 1, y: 0 } }}>
                  {renderField(child as FormField, false)}
                </motion.div>
              ))}
            </motion.div>
          </div>
          <DialogActionsButtonContainer>
            <Button
              color="secondary"
              autoFocus
              variant="contained"
              type="submit"
              disabled={disableSubmit} // ❗ disables the button
              sx={{
                opacity: disableSubmit ? 0.5 : 1, // makes it appear grayed out
                pointerEvents: disableSubmit ? "none" : "auto", // avoids hover effect or tooltips if needed
                cursor: disableSubmit ? "not-allowed" : "pointer",
              }}
            >
              {activeForm?.submitLabel}
            </Button>

          </DialogActionsButtonContainer>
        </form>
      </BootstrapDialogInnerContainer>
    </BootstrapDialog>
  );
}